import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import TableFooter from "@mui/material/TableFooter";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useTheme } from "@mui/material/styles";
import { CSVLink } from "react-csv";
import { genericFilter } from "../../utils/genericFilter";
import MuiInput from "../Input/MuiInput";
import { setSort, setSortBy } from "@store/slices/uiSlice";
import { useDispatch } from "react-redux";

interface EnhancedTableProps {
  tableColumns: Array<{
    key: string;
    label: string;
    numeric?: boolean;
    align?: "right" | "left" | "center";
    sort?: boolean;
  }>;
  page?: number;
  rowsPerPage?: number;
  count?: number;
  tablerows: Array<any>;
  handleChangeTablePage: (newPage: number) => void;
  handleChangeTableSort: () => void;
  footerComponent?: React.ReactNode;
  sortIconHide?: boolean;
}

function EnhancedTableHead(props: any) {
  const { tableColumns, order, orderBy, onRequestSort, sortIconHide } = props;
  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {tableColumns?.map((headCell: any, i: number) => (
          <TableCell
            key={i}
            align={
              headCell?.align
                ? headCell.align
                : headCell.numeric
                ? "right"
                : "left"
            }
            sortDirection={
              orderBy === headCell?.sortKey || headCell?.key ? order : false
            }
            sx={{
              padding: ".75rem 1rem",
              fontSize: "0.875rem",
              whiteSpace: "nowrap"
            }}
          >
            {((!headCell?.sort && headCell?.sort !== false) ||
              headCell?.sort === true) && (
              <TableSortLabel
                key={headCell?.key}
                active={orderBy === headCell?.sortKey || headCell?.key}
                hideSortIcon={sortIconHide ? true : false}
                direction={
                  orderBy === (headCell?.sortKey || headCell?.key)
                    ? order
                    : "desc"
                }
                onClick={createSortHandler(headCell?.sortKey || headCell?.key)}
                sx={{
                  "& .MuiTableSortLabel-icon": {
                    color: "var(--gray-800) !important",
                    opacity: 1,
                    fontSize: "1.125rem"
                  }
                }}
              >
                {headCell?.label}
                {orderBy === headCell?.sortKey || headCell?.key ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
            {headCell?.sort === false ? headCell?.label : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable({
  tableColumns,
  tablerows,
  handleChangeTablePage,
  handleChangeTableSort,
  footerComponent,
  rowsPerPage: rowsPerPageProp,
  page: pageProp,
  count: countprop,
  sortIconHide
}: EnhancedTableProps) {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [allRows, setAllRows] = useState(tablerows);

  useEffect(() => {
    setAllRows(tablerows);
  }, [tablerows]);

  useEffect(() => {
    if (rowsPerPageProp) {
      setRowsPerPage(rowsPerPageProp || 10);
    }
  }, [rowsPerPageProp]);

  useEffect(() => {
    if (pageProp) {
      setPage(pageProp ? pageProp - 1 : 0);
    }
  }, [pageProp]);

  const handleRequestSort = (event: any, property: any) => {
    // const isAsc = orderBy === property && order === "desc";
    setOrder(prev => (prev == "asc" && orderBy == property ? "desc" : "asc"));
    setOrderBy(property);
    handleChangeTableSort();
  };

  const handleChangePage = (event: any, newPage: any) => {
    handleChangeTablePage(newPage + 1);
    if (!pageProp) {
      setPage(newPage);
    }
    setTimeout(() => {
      // scrollToTop();
    }, 0);
  };

  function TablePaginationActions(props: any) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleBackButtonClick = (event: any) => {
      onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: any) => {
      onPageChange(event, page + 1);
    };

    const dispatch = useDispatch();

    useEffect(() => {
      dispatch(setSort(order));
      dispatch(setSortBy(orderBy));
    }, [order, orderBy]);

    return (
      <Box sx={{ flexShrink: 0, ml: 2.5, mr: 6, boxShadow: "none" }}>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === -1 || page === 0}
          aria-label="previous page"
          sx={{
            width: "1.625rem",
            height: "1.625rem",
            border: "1px solid #8A6EF1",
            borderRadius: "0.375rem",
            backgroundColor: "white",
            marginRight: "0.625rem"
          }}
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowRight />
          ) : (
            <KeyboardArrowLeft />
          )}
        </IconButton>
        <div
          style={{
            color: "#212121",
            display: "inline",
            fontSize: "0.875rem"
          }}
        >
          {page + 1 + " of " + Math.ceil(count / rowsPerPage)}
        </div>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
          sx={{
            width: "1.625rem",
            height: "1.625rem",
            border: "1px solid #8A6EF1",
            borderRadius: "0.375rem",
            backgroundColor: "white",
            marginLeft: "0.625rem"
          }}
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowLeft />
          ) : (
            <KeyboardArrowRight />
          )}
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{}}>
      <Paper
        sx={{
          width: "100%",
          mb: 0,
          boxShadow: "none"
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            mb: 0,
            "& .MuiTableContainer-root": {
              marginBottom: "0px"
            }
          }}
        >
          <div className="natural:br-300 rounded-xl table w-full">
            <Table
              aria-labelledby="tableTitle"
              sx={{
                minWidth: 750,
                "& .MuiTableRow-head": {
                  backgroundColor: "#CFDAE733"
                }
              }}
            >
              <EnhancedTableHead
                tableColumns={tableColumns}
                numSelected={0}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={null}
                onRequestSort={handleRequestSort}
                rowCount={allRows?.length}
                sortIconHide={sortIconHide}
              />
              <TableBody>
                {(!allRows || allRows.length === 0) ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns?.length || 1}
                      align="center"
                      sx={{ py: 4, color: "#6B7280", fontSize: "1rem" }}
                    >
                      <Box>No data found</Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  allRows?.map((row: any, index: any) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={index}
                        sx={{
                          cursor: "pointer",
                          "&:not(:last-child)": {
                            borderBottom: "1px solid var(--natural-300)"
                          }
                        }}
                      >
                        {tableColumns?.map((columns: any, index: number) => {
                          return (
                            <TableCell
                              component="td"
                              sx={{
                                padding: ".75rem 1rem",
                                fontSize: "1rem",
                                borderBottom: "0px"
                              }}
                              key={index}
                            >
                              {row[columns?.key]}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <div style={{ width: "100%", display: "flex" }}>
            <div
              style={{
                display: "inline-table",
                width: "100%",
                marginRight: "0",
                marginLeft: "0"
              }}
            >
              <div
                className={`flex ${
                  footerComponent ? "justify-between" : "justify-end"
                } items-center w-full`}
              >
                {footerComponent}
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#212121",
                    marginRight: "1rem"
                  }}
                >
                  Total Count: {countprop || allRows?.length}
                </div>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[10]}
                  count={countprop || allRows?.length}
                  rowsPerPage={rowsPerPage}
                  page={countprop ? page : -1}
                  sx={{
                    "& .MuiTablePagination-displayedRows": {
                      display: "none",
                      marginRight: "0",
                      marginLeft: "0"
                    }, // This above css code hide page numbering (1of4  like)
                    "& .MuiTablePagination-toolbar": {
                      paddingLeft: "0"
                    },

                    "& .MuiBox-root": {
                      marginRight: "0",
                      marginLeft: "0",
                      paddingLeft: "0"
                    },
                    borderBottom: "0px"
                  }}
                  labelRowsPerPage={""}
                  onPageChange={handleChangePage}
                  ActionsComponent={TablePaginationActions}
                />
              </div>
            </div>
          </div>
        </TableContainer>
      </Paper>
    </Box>
  );
}
